import React, { FC, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { AxiosResponse } from 'axios';
import {
  Button,
  ButtonColor,
  ButtonSize,
  Combobox,
  TextInput,
  Option,
  CurrencyInput,
} from '@code/innovance-react-ui-kit';

import {
  ReferenceDataType,
  LoanPaymentPlanSimBaseCalculateResponse,
  LoanPaymentPlanSimCarCalculateResponse,
  LoanPaymentPlanSimAuchanCalculateResponse,
  LoanPaymentPlanSimHolidayCalculateResponse,
  LoanPaymentPlanSimInstantCalculateResponse,
} from 'src/front/api/generated';
import { DocumentStorage, PPSimulationApi } from 'src/front/api/apiInstance';

import displayPDF from 'src/front/common/utils/domUtils';
import useYupResolver from 'src/front/common/hooks/useYupResolver';
import usePRM, { useConditionalPRM } from 'src/front/common/hooks/usePRM';
import { PPSimulationSchema } from 'src/front/common/utils/validationSchemas';
import { formatBlank, formatNumeric, optionFormat } from 'src/front/common/utils/formUtils';

import {
  ButtonRow,
  Container,
  FormElement,
  FormRow,
  InfoRow,
  LongFormRow,
  ResetButton,
  Subtitle,
  Title,
} from 'src/front/components/FormStyles';
import CalculationDetails from './CalculationDetails';

export interface PaymentPlanSimCalculationResult extends LoanPaymentPlanSimBaseCalculateResponse {
  lifeComm?: string;
}

export type PaymentPlanSimCalculationResponse = AxiosResponse<
  | LoanPaymentPlanSimCarCalculateResponse
  | LoanPaymentPlanSimAuchanCalculateResponse
  | LoanPaymentPlanSimHolidayCalculateResponse
  | LoanPaymentPlanSimInstantCalculateResponse
>;

interface FormValues {
  loanType: Option | null;
  retailOutlet: Option | null;
  actionType: Option | null;
  currencyType: Option | null;
  creditPeriod: Option | null;
  goodsTotal: string;
  lifeInsurance: Option | null;
  initialPaymentMethod: Option | null;
  initialPaymentRate: string;
  initialPaymentAmount: string;
  serviceAmount: string;
  equipmentCost: string;
  kasko: string;
  kaskoPeriod: Option | null;
  osago: string;
  osagoPeriod: Option | null;
  dago: string;
  dagoPeriod: Option | null;
  policyCost: Option | null;
}

const defaultValues: FormValues = {
  loanType: null,
  retailOutlet: null,
  actionType: null,
  currencyType: null,
  creditPeriod: null,
  goodsTotal: '',
  lifeInsurance: null,
  initialPaymentMethod: null,
  initialPaymentRate: '',
  initialPaymentAmount: '0',
  serviceAmount: '0',
  equipmentCost: '',
  kasko: '',
  kaskoPeriod: null,
  osago: '',
  osagoPeriod: { label: '1', value: '1' },
  dago: '',
  dagoPeriod: null,
  policyCost: null,
};

const PaymentPlanSimulation: FC = () => {
  const [isInitialPaymentRequired, setIsInitialPaymentRequired] = useState<boolean>(false);
  const [isLoanAndRetailSelected, setLoanAndRetailSelected] = useState<boolean>(false);
  const [isCarLoanSelected, setCarLoanSelected] = useState<boolean>(false);
  const [isGeneralFilled, setIsGeneralFilled] = useState<boolean>(false);
  const [isDetailFilled, setIsDetailFilled] = useState<boolean>(false);
  const [isCalculated, showResult] = useState<boolean>(false);
  const [result, setResult] = useState<PaymentPlanSimCalculationResult>();

  const { t } = useTranslation();

  const resolver = useYupResolver(PPSimulationSchema, {}, { isCarLoanSelected });
  const { register, setValue, watch, getValues, reset, control, errors, trigger } = useForm<
    FormValues
  >({
    defaultValues,
    resolver,
  });

  const loanTypePRM = usePRM(ReferenceDataType.PAYMENTPLANSIMLOANTYPE);
  const retailOutletPRM = usePRM(ReferenceDataType.CREDITSHOPS);
  const [actionTypePRM, setActionTypes] = useState<Option[]>([]);
  const selectedActionType = watch('actionType')?.value;
  const currencyTypePRM = useConditionalPRM(ReferenceDataType.LOANCURRENCYTYPE, selectedActionType);
  const currencySymbol = watch('currencyType')?.value;
  const creditPeriodPRM = useConditionalPRM(ReferenceDataType.LOANCREDITPERIOD, selectedActionType);
  const insurancePRM = usePRM(ReferenceDataType.YESNOTYPE);
  const initialPaymentMethodPRM = usePRM(ReferenceDataType.LOANINITPAYMENTMETHOD);
  const periodPRM = usePRM(ReferenceDataType.ONETOTEN);
  const carPeriodPRM = usePRM(ReferenceDataType.LOANINSURANCEPERIOD);

  const registerFields = (): void => {
    register('loanType');
    register('retailOutlet');
    register('actionType');
    register('currencyType');
    register('creditPeriod');
    register('goodsTotal');
    register('lifeInsurance');
    register('initialPaymentMethod');
    register('initialPaymentRate');
    register('initialPaymentAmount');
    register('serviceAmount');
    register('equipmentCost');
    register('kasko');
    register('kaskoPeriod');
    register('osago');
    register('osagoPeriod');
    register('dago');
    register('dagoPeriod');
    register('policyCost');
  };

  useEffect(() => {
    registerFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getActionTypes = async (): Promise<void> => {
    const { loanType, retailOutlet } = getValues();
    const { data } = await PPSimulationApi.getSimActionCombo({
      loanTypeCode: loanType?.value || '',
      retailOutletCode: retailOutlet?.value || '',
    });

    if (data.result === 'OK') setActionTypes(data.parameterDataList.map(optionFormat));
  };

  const getCalculatedInitialPaymentAmount = async (): Promise<void> => {
    const { actionType, goodsTotal } = getValues();
    if (actionType) {
      const { data } = await PPSimulationApi.getInitialPaymentRate({
        actionTypeCode: actionType?.value,
      });

      if (data.result === 'OK') {
        setValue('initialPaymentMethod', initialPaymentMethodPRM[0]); // Select Percentage
        setValue('initialPaymentRate', data.initialPayment || '0');
        setValue(
          'initialPaymentAmount',
          `${Number(goodsTotal) * 1 + Number(data.initialPayment) / 10}` || '0'
        );

        setIsInitialPaymentRequired(data.initialPayment !== '');
      }
    }
  };

  const getCostKey = (): string => {
    const { loanType } = getValues();

    switch (loanType?.value) {
      case '300550': // Car Loan
        return 'PPSimulation_calculation_carCostTotal_label';
      case '300552': // Holiday Loan
        return 'PPSimulation_calculation_cost_label';
      default:
        return 'PPSimulation_calculation_goodsTotal_label';
    }
  };

  const checkSectionsFilled = (): void => {
    const {
      retailOutlet,
      loanType,
      actionType,
      currencyType,
      creditPeriod,
      goodsTotal,
      lifeInsurance,
      kasko,
      kaskoPeriod,
      osago,
      osagoPeriod,
      policyCost,
    } = getValues();

    const generalSection = Boolean(
      retailOutlet && loanType && actionType && currencyType && creditPeriod
    );
    const detailSection = Boolean(
      generalSection && goodsTotal && (isCarLoanSelected || lifeInsurance)
    );
    const policyCostSection = Boolean(
      detailSection && kasko && kaskoPeriod && osago && osagoPeriod && policyCost
    );

    // If all required fields filled render -> Detail Product Information
    setIsGeneralFilled(generalSection);
    setIsDetailFilled(isCarLoanSelected ? policyCostSection : detailSection);

    // If Car Loan selected -> set default for Policy Cost
    if (isGeneralFilled && !policyCost) setValue('policyCost', insurancePRM[1]);
  };

  const calculatePayment = async (): Promise<void> => {
    const {
      loanType,
      retailOutlet,
      actionType,
      currencyType,
      creditPeriod,
      goodsTotal,
      lifeInsurance,
      initialPaymentMethod,
      initialPaymentRate,
      initialPaymentAmount,
      serviceAmount,
      equipmentCost,
      kasko,
      kaskoPeriod,
      osago,
      osagoPeriod,
      dago,
      dagoPeriod,
      policyCost,
    } = getValues();

    trigger(); // Check errors

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let res: PaymentPlanSimCalculationResponse | undefined;

    const paymentObj = {
      loanTypeCode: loanType?.value || '',
      retailOutletCode: retailOutlet?.value || '',
      actionTypeCode: actionType?.value || '',
      currencyTypeCode: currencyType?.value || '',
      creditPeriodCode: creditPeriod?.value || '',
      additionalServiceAmount: serviceAmount ? formatBlank(serviceAmount) : '0',
      ...(initialPaymentMethod?.value === '0'
        ? {
            initialPaymentRate,
          }
        : {
            initialPaymentAmount: `${Number(formatBlank(initialPaymentAmount))}`,
          }),
    };

    switch (loanType?.value) {
      case '300550': // Car Loan
        res = await PPSimulationApi.calculateCarLoanPaymentPlan({
          ...paymentObj,
          carCost: `${Number(formatBlank(goodsTotal))}`,
          additionalEquipmentCost: `${Number(formatBlank(equipmentCost))}`,
          kaskoAmount: `${Number(formatBlank(kasko))}`,
          kaskoPeriodCode: kaskoPeriod?.value || '',
          osagoAmount: `${Number(formatBlank(osago))}`,
          osagoPeriodCode: osagoPeriod?.value || '',
          dagoAmount: dago ? formatBlank(dago) : '0',
          dagoPeriodCode: dagoPeriod?.value || '',
          policyCostWithLoanAmount: policyCost?.value || '',
        });
        break;
      case '300552': // Holiday Loan
        res = await PPSimulationApi.calculateHolidayLoanPaymentPlan({
          ...paymentObj,
          lifeInsuranceCode: lifeInsurance?.value || '',
          cost: formatBlank(goodsTotal),
        });
        break;
      case '300553': // Instant Loan
        res = await PPSimulationApi.calculateInstantLoanPaymentPlan({
          ...paymentObj,
          lifeInsuranceCode: lifeInsurance?.value || '',
          cost: formatBlank(goodsTotal),
        });
        break;
      case '300554': // Loan with Deposit
        res = await PPSimulationApi.calculateAuchanLoanPaymentPlan({
          ...paymentObj,
          lifeInsuranceCode: lifeInsurance?.value || '',
          cost: formatBlank(goodsTotal),
        });
        break;
    }

    if (res && res.data.result === 'OK') {
      setResult(res.data);
      showResult(true);
    }
  };

  const handleComboboxChange = (fieldName: string) => async (
    item: Option | null
  ): Promise<void> => {
    setValue(fieldName, item);
    const { retailOutlet, loanType } = getValues();

    if (isCalculated) showResult(false);

    switch (fieldName) {
      case 'loanType':
        if (item && retailOutlet) {
          await getActionTypes();
          setLoanAndRetailSelected(true);
        } else setLoanAndRetailSelected(false);

        setCarLoanSelected(item?.value === '300550');

        reset({ ...defaultValues, loanType, retailOutlet });
        registerFields();
        break;
      case 'retailOutlet':
        if (item && loanType) {
          await getActionTypes();
          setLoanAndRetailSelected(true);
        } else setLoanAndRetailSelected(false);

        reset({ ...defaultValues, loanType, retailOutlet });
        registerFields();
        break;
      case 'actionType':
        getCalculatedInitialPaymentAmount();
        break;
    }
  };

  const handleInputChange = (fieldName: string) => (text?: string): void => {
    if (text) setValue(fieldName, text);
  };

  const handlePrintPlan = async (): Promise<void> => {
    const { data } = await PPSimulationApi.printPaymentPlan();

    if (data.result === 'OK') {
      const { data: docData } = await DocumentStorage.getDocument({
        documentId: data.documentId,
      });

      if (docData.result === 'OK') displayPDF(docData.documentData);
    }
  };

  const handleReset = (): void => {
    reset(defaultValues);
    registerFields();

    showResult(false);
    setIsDetailFilled(false);
    setIsGeneralFilled(false);
  };

  useEffect(() => {
    checkSectionsFilled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleComboboxChange, handleInputChange]);

  return (
    <Container>
      <InfoRow>
        <Title>
          <Trans i18nKey="PPSimulation_calculation_paymentPlanSim_title" />
        </Title>
      </InfoRow>
      <InfoRow>
        <Subtitle>
          <Trans i18nKey="PPSimulation_calculation_general_subtitle" />
        </Subtitle>
      </InfoRow>
      <form>
        <FormRow>
          <FormElement>
            <Combobox
              errorMessage={errors.loanType?.message}
              items={loanTypePRM}
              label={t('loanApp_productDetail_loanType_label')}
              name="loanType"
              onChange={handleComboboxChange('loanType')}
              value={watch('loanType')}
              required
            />
          </FormElement>
          <FormElement>
            <Combobox
              errorMessage={errors.retailOutlet?.message}
              items={retailOutletPRM}
              label={t('general_components_shop_label')}
              name="retailOutlet"
              onChange={handleComboboxChange('retailOutlet')}
              value={watch('retailOutlet')}
              required
              searchable
            />
          </FormElement>
        </FormRow>
        {isLoanAndRetailSelected ? (
          <>
            <FormRow>
              <FormElement>
                <Combobox
                  errorMessage={errors.actionType?.message}
                  items={actionTypePRM}
                  label={t('loanApp_productDetail_actionType_label')}
                  name="actionType"
                  onChange={handleComboboxChange('actionType')}
                  value={watch('actionType')}
                  required
                  searchable
                />
              </FormElement>
              <FormElement>
                <Combobox
                  errorMessage={errors.currencyType?.message}
                  items={currencyTypePRM}
                  label={t('loanApp_productDetail_currencyType_label')}
                  name="currencyType"
                  onChange={handleComboboxChange('currencyType')}
                  value={watch('currencyType')}
                  required
                />
              </FormElement>
            </FormRow>
            <FormRow>
              <FormElement>
                <Combobox
                  errorMessage={errors.creditPeriod?.message}
                  items={creditPeriodPRM}
                  label={t('loanApp_productDetail_creditPeriod_label')}
                  name="creditPeriod"
                  onChange={handleComboboxChange('creditPeriod')}
                  value={watch('creditPeriod')}
                  required
                />
              </FormElement>
            </FormRow>
          </>
        ) : null}
        {isGeneralFilled ? (
          <>
            <InfoRow>
              <Subtitle>
                <Trans i18nKey="PPSimulation_calculation_detail_subtitle" />
              </Subtitle>
            </InfoRow>
            <FormRow>
              <FormElement>
                <CurrencyInput
                  currencySymbol={currencySymbol}
                  errorMessage={errors.goodsTotal?.message}
                  label={t(getCostKey())}
                  maxLength={23}
                  onChange={handleInputChange('goodsTotal')}
                  value={watch('goodsTotal')}
                  required
                />
              </FormElement>
              {isCarLoanSelected ? (
                <FormElement>
                  <CurrencyInput
                    currencySymbol={currencySymbol}
                    errorMessage={errors.equipmentCost?.message}
                    label={t('PPSimulation_calculation_additionalEquipmentCost_label')}
                    onChange={handleInputChange('equipmentCost')}
                    value={watch('equipmentCost')}
                  />
                </FormElement>
              ) : (
                <FormElement>
                  <Combobox
                    errorMessage={errors.lifeInsurance?.message}
                    items={insurancePRM}
                    label={t('PPSimulation_calculation_lifeInsurenceOption_label')}
                    name="lifeInsurance"
                    onChange={handleComboboxChange('lifeInsurance')}
                    value={watch('lifeInsurance')}
                    required
                  />
                </FormElement>
              )}
            </FormRow>
            <FormRow>
              <FormElement>
                <Combobox
                  disabled={isInitialPaymentRequired}
                  errorMessage={errors.initialPaymentMethod?.message}
                  items={initialPaymentMethodPRM}
                  label={t('PPSimulation_calculation_initPayMethod_label')}
                  name="initialPaymentMethod"
                  onChange={handleComboboxChange('initialPaymentMethod')}
                  value={watch('initialPaymentMethod')}
                />
              </FormElement>
              <FormElement>
                {watch('initialPaymentMethod')?.value === '1' ? (
                  // Payment method -> Amount
                  <CurrencyInput
                    currencySymbol={currencySymbol}
                    disabled={isInitialPaymentRequired}
                    errorMessage={errors.initialPaymentAmount?.message}
                    label={t('loanApp_productDetail_initialPayAmount_label')}
                    maxLength={12}
                    onChange={handleInputChange('initialPaymentAmount')}
                    value={watch('initialPaymentAmount')}
                  />
                ) : (
                  // Payment method -> Percentage
                  <TextInput
                    disabled={isInitialPaymentRequired}
                    errorMessage={errors.initialPaymentRate?.message}
                    formatter={formatNumeric}
                    label={t('loanApp_productDetail_initialPaymentPercentage_label')}
                    maxLength={12}
                    name="initialPaymentRate"
                    onChange={handleInputChange('initialPaymentRate')}
                    value={watch('initialPaymentRate')}
                  />
                )}
              </FormElement>
            </FormRow>
            <FormRow>
              <FormElement>
                <Controller
                  control={control}
                  name="serviceAmount"
                  render={({ onChange, value }) => (
                    <CurrencyInput
                      currencySymbol={currencySymbol}
                      errorMessage={errors.serviceAmount?.message}
                      label={t('PPSimulation_calculation_serviceAmount_label')}
                      maxLength={19}
                      onChange={onChange}
                      value={value}
                    />
                  )}
                />
              </FormElement>
            </FormRow>
            {isCarLoanSelected ? (
              <>
                <InfoRow>
                  <Subtitle>
                    <Trans i18nKey="PPSimulation_calculation_policyCost_subtitle" />
                  </Subtitle>
                </InfoRow>
                <FormRow>
                  <FormElement>
                    <CurrencyInput
                      currencySymbol={currencySymbol}
                      errorMessage={errors.kasko?.message}
                      label={t('PPSimulation_calculation_kasko_label')}
                      onChange={handleInputChange('kasko')}
                      value={watch('kasko')}
                      required
                    />
                  </FormElement>
                  <FormElement>
                    <Combobox
                      errorMessage={errors.kaskoPeriod?.message}
                      items={carPeriodPRM}
                      label={t('general_components_period_label')}
                      name="kaskoPeriod"
                      onChange={handleComboboxChange('kaskoPeriod')}
                      value={watch('kaskoPeriod')}
                      required
                    />
                  </FormElement>
                </FormRow>
                <FormRow>
                  <FormElement>
                    <CurrencyInput
                      currencySymbol={currencySymbol}
                      errorMessage={errors.osago?.message}
                      label={t('PPSimulation_calculation_osago_label')}
                      onChange={handleInputChange('osago')}
                      value={watch('osago')}
                      required
                    />
                  </FormElement>
                  <FormElement>
                    <Combobox
                      errorMessage={errors.osagoPeriod?.message}
                      items={periodPRM}
                      label={t('general_components_period_label')}
                      name="osagoPeriod"
                      onChange={handleComboboxChange('osagoPeriod')}
                      value={watch('osagoPeriod')}
                      disabled
                      required
                    />
                  </FormElement>
                </FormRow>
                <FormRow>
                  <FormElement>
                    <CurrencyInput
                      currencySymbol={currencySymbol}
                      errorMessage={errors.dago?.message}
                      label={t('PPSimulation_calculation_dago_label')}
                      onChange={handleInputChange('dago')}
                      value={watch('dago')}
                    />
                  </FormElement>
                  <FormElement>
                    <Combobox
                      errorMessage={errors.dagoPeriod?.message}
                      items={carPeriodPRM}
                      label={t('general_components_period_label')}
                      name="dagoPeriod"
                      onChange={handleComboboxChange('dagoPeriod')}
                      value={watch('dagoPeriod')}
                    />
                  </FormElement>
                </FormRow>
                <FormRow>
                  <FormElement>
                    <Combobox
                      errorMessage={errors.policyCost?.message}
                      items={insurancePRM}
                      label={t('PPSimulation_calculation_includeCosts_label')}
                      name="policyCost"
                      onChange={handleComboboxChange('policyCost')}
                      value={watch('policyCost')}
                      required
                    />
                  </FormElement>
                </FormRow>
              </>
            ) : null}
            {isDetailFilled ? (
              <ButtonRow>
                <ResetButton
                  color={ButtonColor.Secondary}
                  onClick={handleReset}
                  size={ButtonSize.Medium}
                  text={t('general_components_reset_button')}
                  type="reset"
                />
                <Button
                  color={ButtonColor.Primary}
                  onClick={calculatePayment}
                  size={ButtonSize.Medium}
                  text={t('PPSimulation_calculation_calculate_label')}
                  type="button"
                />
              </ButtonRow>
            ) : null}
          </>
        ) : null}
        {isCalculated ? (
          <>
            <CalculationDetails carLoan={isCarLoanSelected} result={result} />
            <LongFormRow>
              <Button
                color={ButtonColor.Secondary}
                onClick={handlePrintPlan}
                size={ButtonSize.Medium}
                text={t('PPSimulation_calculation_printPlan_button')}
                type="button"
              />
            </LongFormRow>
          </>
        ) : null}
      </form>
    </Container>
  );
};

export default PaymentPlanSimulation;
